// src/components/student/dashboard/StudentDashboard.js
import React, { useState, useEffect, useMemo } from "react";
import { auth, db } from "../../../firebaseConfig";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

// --- ICONS ---
import { LuLayoutDashboard, LuLogOut, LuChevronLeft, LuChevronRight } from "react-icons/lu";
import { TbCircleLetterS, TbCircleLetterA, TbCircleLetterP } from "react-icons/tb";

// --- IMPORTED COMPONENTS ---
import StudentSATDiagnosticList from "../sat/StudentSATDiagnosticList";
import StudentSATTestTaker from "../sat/StudentSATTestTaker";
import StudentACTDiagnosticList from "../act/StudentACTDiagnosticList";
import StudentACTTestTaker from "../act/StudentACTTestTaker";
import StudentPSATDiagnosticList from "../psat/StudentPSATDiagnosticList";
import StudentPSATTestTaker from "../psat/StudentPSATTestTaker";

// --- STYLES ---
import "./StudentDashboard.css"; 

/* -----------------------------
   Icons
-------------------------------- */
function Icon({ name, active }) {
  const color = active ? "#2D68FE" : "#64748b";
  const size = 18; 

  switch (name) {
    case "sat": return <TbCircleLetterS color={color} size={size} />;
    case "act": return <TbCircleLetterA color={color} size={size} />;
    case "psat": return <TbCircleLetterP color={color} size={size} />;
    case "dashboard": return <LuLayoutDashboard color={color} size={size} />;
    case "logout": return <LuLogOut color={color} size={size} />;
    case "chev": return <LuChevronLeft color={color} size={size} />;
    case "chevRight": return <LuChevronRight color={color} size={size} />;
    default:
      return null;
  }
}

export default function StudentDashboard() {
  const [activeTab, setActiveTab] = useState("sat_dt");
  const [isSidebarExpanded, setIsSidebarExpanded] = useState(true);
  const [userProfile, setUserProfile] = useState({ name: "", email: "" });
  const [activeTestType, setActiveTestType] = useState(null); // 'SAT' or 'ACT'

  // --- LOCKDOWN MODE STATE ---
  // If this is not null, we hide the sidebar and show ONLY the test
  const [activeTestId, setActiveTestId] = useState(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      if (currentUser) {
        try {
          const userDocRef = doc(db, "users", currentUser.uid);
          const userSnap = await getDoc(userDocRef);
          
          if (userSnap.exists()) {
            const data = userSnap.data();
            setUserProfile({
              name: data.name || currentUser.displayName || "Student",
              email: data.email || currentUser.email
            });
          } else {
            setUserProfile({
              name: currentUser.displayName || "Student",
              email: currentUser.email
            });
          }
        } catch (err) {
          console.error("Error fetching user profile:", err);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  const userInitial = useMemo(() => (userProfile.name?.trim()?.[0] || "S").toUpperCase(), [userProfile.name]);

  const handleLogout = () => {
    auth.signOut().catch((err) => console.error("Logout Error:", err));
  };

  // Update handleStartTest to accept type
  const handleStartTest = (testId, type) => {
    setActiveTestId(testId);
    setActiveTestType(type);
  };

  // --- HANDLER: Exit Test ---
  const handleExitTest = () => {
    setActiveTestId(null); // Return to Dashboard
    window.location.reload(); // Optional: Reload to ensure fresh state/data fetch
  };

  // --- RENDER CONTENT ---
  if (activeTestId) {
    if (activeTestType === 'ACT') {
      return <StudentACTTestTaker testId={activeTestId} onExit={handleExitTest} />;
    }
    if (activeTestType === 'PSAT') {
      return <StudentPSATTestTaker testId={activeTestId} onExit={handleExitTest} />;
    }
    return <StudentSATTestTaker testId={activeTestId} onExit={handleExitTest} />;
  }

  const renderMainContent = () => {
    switch(activeTab) {
      case "sat_dt": 
        return (
          <div style={{ overflowY: "auto", height: "100%" }}>
            {/* Pass 'SAT' as type */}
            <StudentSATDiagnosticList onStartTest={(id) => handleStartTest(id, 'SAT')} />
          </div>
        );
      case "act_dt":
        return (
          <div style={{ overflowY: "auto", height: "100%" }}>
            <StudentACTDiagnosticList onStartTest={(id) => handleStartTest(id, 'ACT')} />
          </div>
        );
      case "psat_dt":
        return (
          <div style={{ overflowY: "auto", height: "100%" }}>
            <StudentPSATDiagnosticList onStartTest={(id) => handleStartTest(id, 'PSAT')} />
          </div>
        );
      default:
        return <div style={{ padding: 20 }}>Select a menu item.</div>;
    }
  };

  return (
    <div className="sd-shell">
      {/* SIDEBAR */}
      <aside className={`sd-sidebar ${isSidebarExpanded ? "sd-sidebar--expanded" : "sd-sidebar--collapsed"}`}>
        <div className="sd-sidebarHeader">
          {isSidebarExpanded ? (
            <>
              <div className="sd-brandRow">
                <div className="sd-brandLogo">
                  {/* Reuse your existing logo path */}
                  <img src="/images/logo.png" alt="Logo" style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                </div>
                <div className="sd-brandCol">
                  <div className="sd-brandName">True Education</div>
                  <div className="sd-brandSubtitle">Student Portal</div>
                </div>
              </div>
              <button className="sd-collapseBtn" onClick={() => setIsSidebarExpanded(false)}>
                <Icon name="chev" active={false} />
              </button>
            </>
          ) : (
            <button className="sd-collapseBtn" onClick={() => setIsSidebarExpanded(true)} style={{ margin: "0 auto" }}>
              <Icon name="chevRight" active={false} />
            </button>
          )}
        </div>

        {/* Navigation */}
        <nav className="sd-nav">
          {/* SAT Item */}
          <div 
            className={`sd-navItem ${activeTab === "sat_dt" ? "sd-navItem--active" : ""}`}
            onClick={() => setActiveTab("sat_dt")}
          >
            <div className="sd-navIcon"><Icon name="sat" active={activeTab === "sat_dt"} /></div>
            {isSidebarExpanded && <div className="sd-navLabel">SAT Diagnostic Test</div>}
            {!isSidebarExpanded && <span className="sd-tooltip">SAT DT</span>}
          </div>

          {/* ACT Item */}
          <div
            className={`sd-navItem ${activeTab === "act_dt" ? "sd-navItem--active" : ""}`}
            onClick={() => setActiveTab("act_dt")}
          >
            <div className="sd-navIcon"><Icon name="act" active={activeTab === "act_dt"} /></div>
            {isSidebarExpanded && <div className="sd-navLabel">ACT Diagnostic Test</div>}
            {!isSidebarExpanded && <span className="sd-tooltip">ACT DT</span>}
          </div>

          {/* PSAT Item */}
          <div
            className={`sd-navItem ${activeTab === "psat_dt" ? "sd-navItem--active" : ""}`}
            onClick={() => setActiveTab("psat_dt")}
          >
            <div className="sd-navIcon"><Icon name="psat" active={activeTab === "psat_dt"} /></div>
            {isSidebarExpanded && <div className="sd-navLabel">PSAT Diagnostic Test</div>}
            {!isSidebarExpanded && <span className="sd-tooltip">PSAT DT</span>}
          </div>

        </nav>

        <div style={{ flex: 1 }} />

        <div className="sd-footerNav">
          <div className="sd-navItem" onClick={handleLogout}>
            <div className="sd-navIcon"><Icon name="logout" active={false} /></div>
            {isSidebarExpanded && <div className="sd-navLabel">Logout</div>}
            {!isSidebarExpanded && <span className="sd-tooltip">Logout</span>}
          </div>
        </div>

        {/* User Profile */}
        <div className="sd-userCard">
          <div className="sd-avatar">{userInitial}</div>
          {isSidebarExpanded && (
            <div className="sd-userInfo">
              <div className="sd-userName">{userProfile.name}</div>
              <div className="sd-userEmail">{userProfile.email}</div>
            </div>
          )}
        </div>
      </aside>

      {/* MAIN CONTENT */}
      <div className="sd-main">
        <div className="sd-mainInner">
          <div className="sd-panel">{renderMainContent()}</div>
        </div>
      </div>
    </div>
  );
}